/**
 * @file   src\Pages\EquipmentListing.tsx
 * @brief  Equipment Listing page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { useState, Container, Table, Badge, Button, Breadcrumb, Pagination, useEffect, useOutletContext, useNavigate, useParams, Modal, React } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import EditIcon from '../assets/images/icons/editicon';
import DeleteIcon from '../assets/images/icons/deleteicon';

import AddIcon from '../assets/images/icons/addicon';
import GenerateLink from '../components/GenerateLink';
import { userType } from '../utils/enums';
import { AppContextType } from '../interfaces/generalInterface';
import { PromotionItem, PromotionListItem } from '../interfaces/promotionInterface';
import { pageURLs } from '../utils/pageUrls';
import { CompanyApi, PromotionApi } from '../utils/apiUrls';
import axios from '../config/axiosConfig';
import SmallLoader from '../components/SmallLoader';
import { MessageToaster } from '../utils/toastUtil';
import moment from "moment-timezone"

const ManagePromotions = (props: any) => {

    const carwash_owner = (props.userType === userType['CARWASH-OWNER']) ? true : false;
    const navigate = useNavigate()

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    const [showPromotionDeletepoup, setPromotionDeletepoup] = useState<boolean>(false);

    // State to company details
    const [company, setCompany] = useState({
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: ""
    });

    const toaster = new MessageToaster();

    const [companyDetails, setCompanyDetails] = useState([]);

    const [carwashGuid, setCarwashGuid] = useState<string | null>(null);

    const [totalPages, setTotalPages] = useState(1);

    const param = useParams();

    const [loading, setLoading] = useState(true);

    // State to handle listed promotions
    const [promotions, setPromotions] = useState<PromotionItem[]>([])

    const [currentPage, setCurrentPage] = useState(1);
    const [selected_promo_for_delete, setSelected_promo_for_delete] = useState<any>(0);
    const [selected_promo_name_for_delete, setSelected_promo_name_for_delete] = useState<any>('');
    const [selected_promo_location_for_delete, setSelected_promo_location_for_delete] = useState<any>(0);
    const [subscriptionActive, setSubscriptionActive] = useState(0);

    useEffect(() => {
        getCompanyAddress()
        getCompany()
        //  getPromotions(currentPage)
    }, [])

    // Fetch location list when carwashGuid or currentPage changes
    useEffect(() => {
        if (carwashGuid) {
            getPromotions(currentPage);
        }
    }, [carwashGuid, currentPage])

    useEffect(() => {
        handleUpdate()
    }, [company])

    // Update the data
    const handleUpdate = async () => {
        await updateData({ heading: props.userType !== userType["CARWASH-OWNER"] ? company.companyName : "Promotions", url: props.userType !== userType["CARWASH-OWNER"] ? `/superadmin/${pageURLs.carwash_locations}/${param.id}` : "", showBackButton: props.userType !== userType["CARWASH-OWNER"] ? true : false, showMainMenu: true, activeLink: props.userType !== userType["CARWASH-OWNER"] ? "manage-car-wash" : "" });
    };

    // To get company details
    const getCompanyAddress = async () => {
        try {
            const id = param.id ? parseInt(param.id) : 0;
            const { data } = (!carwash_owner) ? await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id) : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS);
            if (data?.error === 0) {
                setCompany(data.data);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Function to fetch company information
    const getCompany = async () => {
        try {
            const param1 = `${param.id}`;
            const { data } = (!carwash_owner) ?
                await axios.get(CompanyApi.GET_COMPANY + param1) :
                await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);
            setCompanyDetails(data.data)
            setCarwashGuid(data.data.carWashGUID);
            setSubscriptionActive(data.data.subscriptionActive);
        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };


    // Get list of promotions
    const getPromotions = async (pageNo = 1) => {
        try {

            const { data } = (!carwash_owner) ?
                await axios.get(PromotionApi.GET_PROMOTIONS + carwashGuid + `?pageNo=${pageNo}`) :
                await axios.get(PromotionApi.CARWASH_OWNER_GET_PROMOTIONS + `?pageNo=${pageNo}`)
            if (data?.error === 0 && Array.isArray(data?.data?.list)) {
                const promotion_list: PromotionListItem[] = data.data.list;
                if (promotion_list.length > 0) {
                    // Transform the array to match the structure of the Location interface
                    const transformedArray: PromotionItem[] = promotion_list.map(item => ({
                        assignedlocation: item.locationName,
                        washname: item.wshName,
                        promotionname: item.promotionName,
                        generatecode: item.promotionCode,
                        discounttype: item.discountType,
                        discount_value: item.discountValue,
                        validFrom: item.validFrom,
                        validTo: item.validTo,
                        promotionId: item.promotionID,
                        locationId: item.locationId
                    }));
                    setPromotions(transformedArray)
                    setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
                } else {
                    console.log('Promotion list is empty');
                    setPromotions([]);  // Set to an empty array if promotion_list is empty
                }
            } else {
                console.log('Promotion list is not available or is not an array');
                // setSubscriptions([]);  // Set to an empty array if data is not available
            }

            setLoading(false)
        } catch (error: any) {
            console.error('Error fetching company list:', error.message);
            setLoading(false)
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }


    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };

    const deletePromotion = async () => {
        try {
            const promotionId = selected_promo_for_delete;
            const locationId = selected_promo_location_for_delete;
            const { data } = (!carwash_owner) ?
                await axios.post(PromotionApi.DELETE_PROMOTION, { locationId: locationId, promotionId: promotionId })
                :
                await axios.post(PromotionApi.CARWASH_OWNER_DELETE_PROMOTION, { locationId: locationId, promotionId: promotionId })
            if (data?.error === 0) {
                // Reload promotion list
                hideshowPromotionDeleteModal()
                getPromotions(currentPage)
            }
            else {
                // show error message
                hideshowPromotionDeleteModal()
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        } finally {
            setLoading(false)
        }
    }


    // To format date
    const formatDate = (createdDate: string) => {
        const date = moment(createdDate);
        const formattedDate = date.format('MM/DD/YYYY');
        return formattedDate
    }

    //Hide ppopup
    const hideshowPromotionDeleteModal = () => {
        setPromotionDeletepoup(false);
    }


    const DeletebuttonClick = (delete_promo_id: number | undefined, delete_location_id: number | undefined, delete_promo_name: string | undefined) => {
        setSelected_promo_for_delete(delete_promo_id);
        setSelected_promo_location_for_delete(delete_location_id);
        setSelected_promo_name_for_delete(delete_promo_name);
        setPromotionDeletepoup(true);
    }

    return (

        <div className='manage-carwash-container manage-subscription-container'>
            <Container>
                {props.userType !== userType["CARWASH-OWNER"] ?
                    <Breadcrumb>
                        <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_carwash}`) }}>Manage Car Washes</Breadcrumb.Item>
                        <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.carwash_locations}/${param.id}`) }}> {company.companyName}</Breadcrumb.Item>
                        <Breadcrumb.Item active> Manage Promotions</Breadcrumb.Item>
                    </Breadcrumb> : null}

                {props.userType !== userType["CARWASH-OWNER"] ?
                    <div className="manage-activity-wrap d-flex justify-content-between align-items-center">
                        <GenerateLink id={param.id} companyDetails={companyDetails} />
                        <div className="activity-status d-flex">
                            {/* <CarWashStatus carWashId={param.id} status={true} companyName={company.companyName} /> */}
                            <div className="stat-group d-flex align-items-center">
                                <p>Subscription Status</p>
                                <Badge className={subscriptionActive === 1 ? 'active' : 'inactive'}>
                                    {subscriptionActive === 1 ? 'Active' : (subscriptionActive === 0) ? 'Inactive' : 'N/A'}
                                </Badge>
                            </div>

                        </div>
                    </div> : null}

                <div className="manage-header-block d-flex justify-content-end">
                    <Button
                        title="Add New Promotion"
                        type="submit"
                        variant="primary"
                        className="primary-btn ms-auto d-flex align-items-center"
                        onClick={() => props.userType !== userType["CARWASH-OWNER"] ? navigate(`/superadmin/${pageURLs.add_promotion}/${param.id}`) : navigate(`/${pageURLs.add_promotion}`)}
                    >
                        Add New Promotion
                        <AddIcon />
                    </Button>
                </div>

                <div className="cw-table-wrap">
                    <Table responsive className='m-0'>
                        <thead>
                            <tr>
                                <th>Assigned Location</th>
                                <th>Assigned Wash Name</th>
                                <th>Promotion Name</th>
                                <th>Promo Code</th>
                                <th>Discount Type</th>
                                <th>Value ($ or %)</th>
                                <th>Valid From</th>
                                <th>Valid To</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {promotions.length ?
                                (promotions.map((item, index) =>
                                (
                                    <tr key={index}>
                                        <td>{item.assignedlocation}</td>
                                        <td>{item.washname}</td>
                                        <td>{item.promotionname}</td>
                                        <td>{item.generatecode}</td>
                                        <td>{item.discounttype === 1 ? "Amount" : "Percentage"}</td>
                                        <td>{item.discounttype === 1 ? "$" : null} {item.discount_value}{item.discounttype === 1 ? null : "%"}</td>
                                        <td>{formatDate(item.validFrom)}</td>
                                        <td>{formatDate(item.validTo)}</td>
                                        <td className='nowrap'>
                                            <div className="active-group d-flex align-items-center">
                                                <Button variant="light" className="light-btn action-btn" title="Edit" onClick={() => props.userType !== userType["CARWASH-OWNER"] ? navigate(`/superadmin/${pageURLs.edit_promotion}/${param.id}/${item.promotionId}`) : navigate(`/${pageURLs.edit_promotion}/${item.promotionId}`)}><EditIcon /></Button>
                                                <Button variant="light" className="light-btn action-btn" title="Delete" onClick={() => DeletebuttonClick(item.promotionId, item.locationId, item.promotionname)}><DeleteIcon /></Button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                                )
                                ) :
                                (
                                    <tr>
                                        <td style={{ textAlign: 'center' }} colSpan={9} >No data found.</td>
                                    </tr>
                                )}

                        </tbody>
                    </Table>
                    {promotions.length ? <div className="mt-3 d-flex justify-content-end">
                        <Pagination>
                            <Pagination.First
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(1)}
                            />
                            <Pagination.Prev
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            />
                            {getVisiblePageNumbers().map((pageNumber) => (
                                <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === currentPage}
                                    onClick={() => handlePageChange(pageNumber)}
                                >
                                    {pageNumber}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            />
                            <Pagination.Last
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(totalPages)}
                            />
                        </Pagination>
                    </div> : null}
                    {/* <div className="mt-3 d-flex justify-content-end">
                        <Pagination>
                            <Pagination.Prev />
                            <Pagination.Item active>{1}</Pagination.Item>
                            <Pagination.Item>{2}</Pagination.Item>
                            <Pagination.Item>{3}</Pagination.Item>
                            <Pagination.Next />
                        </Pagination>
                    </div> */}
                </div>

            </Container>
            {loading && <SmallLoader />}

            <Modal
                show={showPromotionDeletepoup}
                onHide={hideshowPromotionDeleteModal}
                centered
                className='add-modal'
            >
                <Modal.Body className='text-center'>
                    <h5 className='f-bold text-center'>Do you want to delete the promotion &apos;{selected_promo_name_for_delete}&apos;?</h5>
                    <div className="btn-sec d-grid">
                        <Button variant="light" className="secondary-btn" onClick={hideshowPromotionDeleteModal}>Not Now</Button>
                        <Button variant="primary" className='primary-btn btn-main m-0' onClick={deletePromotion}>Yes</Button>
                    </div>
                </Modal.Body>
            </Modal>

        </div>
    )
}

export default ManagePromotions;
